export type PublicAuthChallengeAction =
  | "forgot-password"
  | "register"
  | "reset-password"
  | "resend-verification"
  | "verify-email";

export type PublicAuthChallengeTokenProvider = (
  action: PublicAuthChallengeAction,
) => Promise<string | undefined>;

export const PUBLIC_AUTH_CHALLENGE_FAILURE_MESSAGE =
  "Unable to verify this request. Please refresh and try again.";

const PUBLIC_AUTH_CHALLENGE_FAILURE_CODES = new Set([
  "PUBLIC_AUTH_CHALLENGE_FAILED",
  "PUBLIC_AUTH_CHALLENGE_UNAVAILABLE",
]);

let tokenProvider: PublicAuthChallengeTokenProvider | null = null;

export class PublicAuthChallengeError extends Error {
  readonly code = "PUBLIC_AUTH_CHALLENGE_FAILED";

  constructor() {
    super(PUBLIC_AUTH_CHALLENGE_FAILURE_MESSAGE);
    this.name = "PublicAuthChallengeError";
  }
}

export function setPublicAuthChallengeTokenProvider(
  provider: PublicAuthChallengeTokenProvider | null,
): void {
  tokenProvider = provider;
}

export async function getPublicAuthChallengeToken(
  action: PublicAuthChallengeAction,
): Promise<string | undefined> {
  if (!tokenProvider) {
    return undefined;
  }

  const token = await tokenProvider(action);
  const trimmedToken = token?.trim();
  return trimmedToken || undefined;
}

export function isPublicAuthChallengeFailure(error: unknown): boolean {
  if (error instanceof PublicAuthChallengeError) {
    return true;
  }

  if (error === null || typeof error !== "object") {
    return false;
  }

  const code = Reflect.get(error, "code");
  return (
    typeof code === "string" && PUBLIC_AUTH_CHALLENGE_FAILURE_CODES.has(code)
  );
}

export function getPublicAuthChallengeFailureMessage(): string {
  return PUBLIC_AUTH_CHALLENGE_FAILURE_MESSAGE;
}
